/**
 * A roster project KEY shown in the reader's language.
 *
 * A thin hook over the pure `projectLabel` (rosterProjectNames.ts), reading the
 * served `db.projectNames` and the i18n language the same way Setup does. The
 * key stays the value everywhere -- filters, sorting, selection and payloads --
 * only the text a person reads goes through this.
 */
import { useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { namesInReach, projectLabel, type ProjectNames } from '../rosterProjectNames'
import { RosterContext, useRoster } from './store'

export function useProjectLabel(): (key: string) => string {
  const { db } = useRoster()
  const { i18n } = useTranslation()
  const language = i18n.resolvedLanguage ?? i18n.language
  const names = db.projectNames
  return useCallback((key: string) => projectLabel(names, key, language), [names, language])
}

/**
 * The same, for a page that may render OUTSIDE the roster section (My
 * schedule is also the self-service home page). With no roster document in
 * reach it reads `served` -- the `projectNames` the page's own response carries
 * (`/api/roster/me` and `/me/project`) -- and with neither a key is shown
 * as itself.
 */
export function useOptionalProjectLabel(
  served?: Record<string, ProjectNames>,
): (key: string) => string {
  const store = useContext(RosterContext)
  const { i18n } = useTranslation()
  const language = i18n.resolvedLanguage ?? i18n.language
  const names = namesInReach(store?.db.projectNames, served)
  return useCallback((key: string) => projectLabel(names, key, language), [names, language])
}
